import React, { useEffect, useState } from 'react';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { getPLineProductType, getProductDisplayName } from 'global/utils/election/productLineElection';
import { formatCurrencyPerMonth } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { AlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { useFirstRender } from '@insurancemenu/imenu-components-lib';
import { productLineActionButtonConfig } from 'global/utils/election/productLineActionButtonConfig';
import { SubscriberPLineCardProps } from '../../types';
import DependentsSection from '../../../common/DependentsSection';
import ProductSelect from './ProductSelect';
import {
  ProductLineCardStyled,
  InfoSectionStyled,
  RateSectionStyled,
  RateStyled,
  TooltipStyled,
  RateActionButtonStyled,
} from './styles';
import {
  SUB_PRODUCT_LINE_CARD_TEST_ID,
  SUB_NOT_EDITABLE_PRODUCT_SELECT_TEST_ID,
  SUB_TOTAL_RATE_VALUE_TEST_ID,
  SUB_RATE_BUTTON_TEST_ID,
} from '../../../constants';

const SubscriberPLineCard: React.FC<SubscriberPLineCardProps> = (props) => {
  const {
    subscriberId,
    dependents,
    isEditable,
    planOptions,
    totalPremiumRate,
    currentChoice: { productLineId, productId, productName, dependentIds },
    isValid,
    productLineType,
    formId,
    showRateButton,
    updateProduct,
    updateDependentsChoice,
    onShowDetails,
    onPLineRateChange,
  } = props;

  if (!productLineId) {
    throw new Error('Product line ID in currentChoice is missing');
  }
  const [selectedDependentIds, setSelectedDependentIds] = useState(dependentIds || []);
  const firstRender = useFirstRender();

  const rateButtonConfig = productLineActionButtonConfig[productLineType]?.[isValid ? 'valid' : 'invalid'] || null;

  useEffect(() => {
    if (!firstRender && updateDependentsChoice) {
      const info = { subscriberId, productLineId, dependentIds: selectedDependentIds };
      updateDependentsChoice(info);
    }
  }, [selectedDependentIds.length]);

  const onProductChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    updateProduct?.({
      subscriberId,
      selectedPlanId: e.target.value,
      selectedPlanName: planOptions.find(({ value }) => value === e.target.value)?.label || '',
      productLineId,
    });
  };

  const onDependentChange = (dependentId: string): void => {
    if (selectedDependentIds.includes(dependentId)) {
      const updatedDependents = selectedDependentIds.filter((id) => id !== dependentId);
      setSelectedDependentIds(updatedDependents);
    } else {
      setSelectedDependentIds([...selectedDependentIds, dependentId]);
    }
  };

  const onRateChange = (): void => {
    onPLineRateChange?.(formId || '', productId);
  };

  return (
    <ProductLineCardStyled
      $isInvalid={!isValid}
      $pLineType={getPLineProductType(productId)}
      data-test-id={`${SUB_PRODUCT_LINE_CARD_TEST_ID}-${productLineId}`}
    >
      <InfoSectionStyled>
        {/* Product select section */}
        {isEditable ? (
          <ProductSelect
            subscriberId={subscriberId}
            productLineId={productLineId}
            productId={productId}
            planOptions={planOptions}
            isEditable={isEditable}
            onShowDetails={onShowDetails}
            onProductChange={onProductChange}
          />
        ) : (
          <TooltipStyled
            label={getProductDisplayName(productId, productName)}
            alignment={AlignmentTypes.BOTTOM_START}
            data-test-id={SUB_NOT_EDITABLE_PRODUCT_SELECT_TEST_ID}
          >
            <ProductSelect
              subscriberId={subscriberId}
              productLineId={productLineId}
              productId={productId}
              planOptions={planOptions}
              isEditable={isEditable}
              onShowDetails={onShowDetails}
              onProductChange={onProductChange}
            />
          </TooltipStyled>
        )}

        {/* Dependents section */}
        <DependentsSection
          areDependentsIneligible={
            productLineType === 'WAIVED' || PRODUCT_LINES_CONTENT_MAP[productLineId]?.hideDependents
          }
          dependentsList={dependents}
          isEditable={isEditable}
          onDependentChange={onDependentChange}
          selectedDependentIds={selectedDependentIds}
          productLineId={productLineId}
        />
      </InfoSectionStyled>

      {/* Rate section */}
      <RateSectionStyled>
        <RateStyled $isInvalid={!isValid} data-test-id={SUB_TOTAL_RATE_VALUE_TEST_ID}>
          {formatCurrencyPerMonth(totalPremiumRate)}
        </RateStyled>
        {showRateButton && rateButtonConfig && formId && (
          <RateActionButtonStyled
            type="text"
            uppercase
            color="primary"
            leftIconProp={rateButtonConfig.icon}
            onClick={onRateChange}
            data-test-id={SUB_RATE_BUTTON_TEST_ID}
          >
            {rateButtonConfig.title}
          </RateActionButtonStyled>
        )}
      </RateSectionStyled>
    </ProductLineCardStyled>
  );
};

export default SubscriberPLineCard;
